import React, { useRef } from "react";
import dayjs from "dayjs";
import downloadToPdf from "../../libs/downloadPdf";
import { formatCurrencyAmount } from "../../utils/currency";
import { Button } from "../common/Button";
import CustomModal from "../common/CustomModal";
import Loader from "../common/Loader";
import {
  Actions,
  ErrorText,
  ModalBody,
  ReceiptPaper,
  ReceiptStatus,
  ReceiptTitle,
  Row,
} from "./receiptModalStyles";
import { PayoutReceipt } from "./types";

type Props = {
  open: boolean;
  loading: boolean;
  error?: string | null;
  receipt: PayoutReceipt | null;
  onClose: () => void;
};

const formatValue = (value: string | number | null | undefined) => {
  if (value == null || value === "") {
    return "—";
  }

  return String(value);
};

const formatReceiptDate = (value: string | null | undefined) => {
  if (!value) {
    return "—";
  }

  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format("DD.MM.YYYY - HH:mm");
};

const NotificationReceiptModal = ({
  open,
  loading,
  error,
  receipt,
  onClose,
}: Props) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const isFailed = receipt?.status === "FAILED";

  return (
    <CustomModal
      isModalVisible={open}
      onClose={onClose}
      width="480px"
      noPadding={false}
    >
      <ModalBody>
        <Loader isLoading={loading} isComponentType />

        {error && <ErrorText>{error}</ErrorText>}

        {receipt && !loading && !error && (
          <ReceiptPaper ref={receiptRef}>
            <ReceiptTitle>Receipt</ReceiptTitle>
            <ReceiptStatus $failed={isFailed}>
              {receipt.status}
            </ReceiptStatus>

            <Row>
              <span>Amount</span>
              <span>
                {formatCurrencyAmount(receipt.currency, receipt.amount)}
              </span>
            </Row>
            <Row>
              <span>Fee</span>
              <span>
                {receipt.fee != null
                  ? formatCurrencyAmount(receipt.currency, receipt.fee)
                  : "—"}
              </span>
            </Row>
            <Row>
              <span>Request ID</span>
              <span>{formatValue(receipt.requestId)}</span>
            </Row>
            <Row>
              <span>Reference</span>
              <span>{formatValue(receipt.reference)}</span>
            </Row>
            <Row>
              <span>Beneficiary</span>
              <span>{formatValue(receipt.accountName)}</span>
            </Row>
            <Row>
              <span>Account</span>
              <span>{formatValue(receipt.accountNumber)}</span>
            </Row>
            <Row>
              <span>Bank</span>
              <span>{formatValue(receipt.bankName)}</span>
            </Row>
            <Row>
              <span>Business</span>
              <span>{formatValue(receipt.businessName)}</span>
            </Row>
            <Row>
              <span>Narration</span>
              <span>{formatValue(receipt.narration)}</span>
            </Row>
            <Row>
              <span>Session ID</span>
              <span>{formatValue(receipt.sessionId)}</span>
            </Row>
            {isFailed && (
              <Row>
                <span>Failure reason</span>
                <span>{formatValue(receipt.failureReason)}</span>
              </Row>
            )}
            <Row>
              <span>Created</span>
              <span>{formatReceiptDate(receipt.createdAt)}</span>
            </Row>
            <Row>
              <span>Completed</span>
              <span>{formatReceiptDate(receipt.completedAt)}</span>
            </Row>
          </ReceiptPaper>
        )}

        <Actions>
          {receipt && !loading && !error && (
            <Button
              label="Download PDF"
              theme="secondary"
              height="40px"
              width="140px"
              onClick={() =>
                downloadToPdf(
                  receiptRef,
                  `receipt-${receipt.requestId || "payout"}`
                )
              }
            />
          )}
          <Button
            label="Close"
            theme="primary"
            height="40px"
            width="100px"
            onClick={onClose}
          />
        </Actions>
      </ModalBody>
    </CustomModal>
  );
};

export default NotificationReceiptModal;
